import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import ProLayout from '@/components/layouts/ProLayout';
import { 
  LayoutDashboard, UserPlus, Users, 
  GraduationCap, Trophy, Star, TrendingUp, ChevronRight,
  Calculator, BookOpen, FlaskConical, Mail, Bell, BellOff,
  Calendar, FileText, Download, BarChart3
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const ParentDashboard = () => {
  const { user, api } = useAuth();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailPrefs, setEmailPrefs] = useState({
    weekly_summary: true,
    achievement_alerts: true,
    session_reminders: true
  });
  const [recentAchievements, setRecentAchievements] = useState([]);
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [selectedChildForSummary, setSelectedChildForSummary] = useState(null);

  const sidebarItems = [
    { path: '/parent', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/parent/register-child', label: 'Register Child', icon: UserPlus },
  ];

  useEffect(() => {
    const fetchChildren = async () => {
      try {
        const response = await api.get('/parent/children');
        setChildren(response.data.children);
      } catch (error) {
        console.error('Error fetching children:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchChildren();
    fetchEmailPrefs();
    fetchRecentAchievements();
  }, [api]);

  const fetchEmailPrefs = async () => {
    try {
      const res = await api.get('/parent/email-preferences');
      setEmailPrefs(res.data);
    } catch (error) {
      console.error('Error fetching email preferences:', error);
    }
  };

  const fetchRecentAchievements = async () => {
    try {
      const res = await api.get('/parent/achievement-notifications');
      setRecentAchievements(res.data.achievements || []);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    }
  };

  const fetchWeeklySummary = async (childId) => {
    try {
      const res = await api.get(`/parent/child/${childId}/weekly-summary`);
      setWeeklySummary(res.data.summary);
      setSelectedChildForSummary(childId);
    } catch (error) {
      toast.error('Failed to load weekly summary');
    }
  };

  const updateEmailPref = async (key, value) => {
    try {
      const newPrefs = { ...emailPrefs, [key]: value };
      setEmailPrefs(newPrefs);
      await api.put('/parent/email-preferences', newPrefs);
      toast.success('Email preferences updated');
    } catch (error) {
      toast.error('Failed to update preferences');
    }
  };

  const subjectIcons = {
    maths: { icon: Calculator, color: 'amber' },
    english: { icon: BookOpen, color: 'pink' },
    science: { icon: FlaskConical, color: 'emerald' },
  };

  const keyStageLabels = {
    ks1: 'Key Stage 1',
    ks2: 'Key Stage 2',
    ks3: 'Key Stage 3',
    ks4: 'Key Stage 4 (GCSE)',
    esol: 'ESOL',
    '11plus': '11+ Preparation'
  };

  return (
    <ProLayout title="Parent Dashboard" sidebarItems={sidebarItems}>
      <div data-testid="parent-dashboard">
        {/* Welcome Section */}
        <div className="pro-card bg-gradient-to-r from-emerald-500 to-teal-600 text-white mb-8">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <h1 className="font-heading text-2xl font-bold mb-2">
                Welcome, {user?.first_name}!
              </h1>
              <p className="text-emerald-100">
                Track your children's learning progress and achievements
              </p>
            </div>
            <Link to="/parent/register-child">
              <Button 
                className="bg-white text-emerald-700 hover:bg-emerald-50"
                data-testid="register-child-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Register Child
              </Button>
            </Link>
          </div>
        </div>

        {/* Children Section */}
        <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-emerald-600" />
          Your Children
        </h2>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-600 border-t-transparent"></div>
          </div>
        ) : children.length === 0 ? (
          <div className="pro-card text-center py-12">
            <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-slate-700 mb-2">
              No Children Registered
            </h3>
            <p className="text-slate-500 mb-6">
              Register your child to get started with their learning journey
            </p>
            <Link to="/parent/register-child">
              <Button className="bg-emerald-600 hover:bg-emerald-700">
                <UserPlus className="w-4 h-4 mr-2" />
                Register Your First Child
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {children.map((child) => (
              <Link 
                key={child.id} 
                to={`/parent/child/${child.id}`}
                data-testid={`child-card-${child.id}`}
              >
                <div className="pro-card group cursor-pointer hover:border-emerald-500 border-2 border-transparent transition-all">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center">
                      <span className="font-bold text-emerald-700 text-xl">
                        {child.first_name?.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-bold text-slate-800">
                        {child.first_name} {child.last_name}
                      </h3>
                      <p className="text-sm text-slate-500">
                        Year {child.year_group} • {keyStageLabels[child.key_stage] || child.key_stage?.toUpperCase()}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-purple-600 mb-1">
                        <Star className="w-5 h-5" />
                        <span className="font-bold text-lg">{child.total_points || 0}</span>
                      </div>
                      <p className="text-xs text-slate-500">Points</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-amber-600 mb-1">
                        <Trophy className="w-5 h-5" />
                        <span className="font-bold text-lg">{child.achievements_count || 0}</span>
                      </div>
                      <p className="text-xs text-slate-500">Badges</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-emerald-600 mb-1">
                        <TrendingUp className="w-5 h-5" />
                        <span className="font-bold text-lg">{child.lessons_completed || 0}</span>
                      </div>
                      <p className="text-xs text-slate-500">Lessons</p>
                    </div>
                  </div>

                  {/* Subjects */}
                  <div className="border-t pt-4">
                    <p className="text-xs font-semibold text-slate-500 mb-2">ENROLLED SUBJECTS</p>
                    <div className="flex gap-2 flex-wrap">
                      {(child.allocated_subjects || []).map(subj => {
                        const subjInfo = subjectIcons[subj] || { icon: BookOpen, color: 'gray' };
                        return (
                          <span 
                            key={subj} 
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold capitalize bg-${subjInfo.color}-100 text-${subjInfo.color}-700`}
                          >
                            <subjInfo.icon className="w-3 h-3" />
                            {subj}
                          </span>
                        );
                      })}
                      {(!child.allocated_subjects || child.allocated_subjects.length === 0) && (
                        <span className="text-xs text-slate-400">No subjects allocated yet</span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Tips Section */}
        <div className="mt-8 pro-card bg-slate-50">
          <h3 className="font-heading text-lg font-bold text-slate-800 mb-4">Tips for Parents</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                <span className="text-xl">📚</span>
              </div>
              <h4 className="font-semibold text-slate-700 mb-1">Regular Practice</h4>
              <p className="text-sm text-slate-500">15-20 minutes daily is more effective than long irregular sessions</p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-3">
                <span className="text-xl">🎯</span>
              </div>
              <h4 className="font-semibold text-slate-700 mb-1">Set Goals</h4>
              <p className="text-sm text-slate-500">Help your child set weekly targets for lessons and points</p>
            </div>
            <div className="bg-white p-4 rounded-lg">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-3">
                <span className="text-xl">🏆</span>
              </div>
              <h4 className="font-semibold text-slate-700 mb-1">Celebrate Progress</h4>
              <p className="text-sm text-slate-500">Acknowledge achievements and new badges earned</p>
            </div>
          </div>
        </div>

        {/* Weekly Summary Section */}
        {children.length > 0 && (
          <div className="mt-8">
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-emerald-600" />
              Weekly Progress Summary
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Select Child for Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">View Child's Weekly Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {children.map(child => (
                      <Button
                        key={child.id}
                        variant={selectedChildForSummary === child.id ? "default" : "outline"}
                        className="w-full justify-start"
                        onClick={() => fetchWeeklySummary(child.id)}
                        data-testid={`summary-btn-${child.id}`}
                      >
                        <span className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center mr-3 text-sm font-bold text-emerald-700">
                          {child.first_name?.charAt(0)}
                        </span>
                        {child.first_name}'s Summary
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Summary Display */}
              {weeklySummary ? (
                <Card className="bg-gradient-to-br from-emerald-50 to-teal-50">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center justify-between">
                      {weeklySummary.child_name}'s Week
                      <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-1" />
                        PDF
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-white rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-emerald-600">{weeklySummary.summary?.lessons_completed || 0}</div>
                        <div className="text-xs text-slate-600">Lessons</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-purple-600">{weeklySummary.summary?.games_played || 0}</div>
                        <div className="text-xs text-slate-600">Games</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-amber-600">{weeklySummary.summary?.total_points_earned || 0}</div>
                        <div className="text-xs text-slate-600">Points Earned</div>
                      </div>
                      <div className="bg-white rounded-lg p-3 text-center">
                        <div className="text-2xl font-bold text-pink-600">{weeklySummary.summary?.achievements_earned || 0}</div>
                        <div className="text-xs text-slate-600">New Badges</div>
                      </div>
                    </div>
                    
                    {/* Areas for Improvement */}
                    {weeklySummary.areas_for_improvement?.length > 0 && (
                      <div className="bg-amber-50 rounded-lg p-3">
                        <h4 className="font-semibold text-sm mb-2 text-amber-800">Areas to Focus On</h4>
                        {weeklySummary.areas_for_improvement.map((area, idx) => (
                          <div key={idx} className="text-xs text-amber-700 mb-1">
                            • {area.subject}: {area.topic} ({area.average_score}%)
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="flex items-center justify-center">
                  <CardContent className="text-center py-8">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Select a child to view their weekly summary</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* Email Preferences */}
        <div className="mt-8">
          <h2 className="font-heading text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            Email Notifications
          </h2>
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="font-medium text-slate-800">Weekly Progress Summary</p>
                      <p className="text-sm text-slate-500">Receive a weekly report of your child's progress</p>
                    </div>
                  </div>
                  <Switch
                    checked={emailPrefs.weekly_summary}
                    onCheckedChange={(checked) => updateEmailPref('weekly_summary', checked)}
                    data-testid="weekly-summary-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Trophy className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="font-medium text-slate-800">Achievement Alerts</p>
                      <p className="text-sm text-slate-500">Get notified when your child earns a badge</p>
                    </div>
                  </div>
                  <Switch
                    checked={emailPrefs.achievement_alerts}
                    onCheckedChange={(checked) => updateEmailPref('achievement_alerts', checked)}
                    data-testid="achievement-alerts-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-slate-500" />
                    <div>
                      <p className="font-medium text-slate-800">Session Reminders</p>
                      <p className="text-sm text-slate-500">Reminders about upcoming tutoring sessions</p>
                    </div>
                  </div>
                  <Switch
                    checked={emailPrefs.session_reminders}
                    onCheckedChange={(checked) => updateEmailPref('session_reminders', checked)}
                    data-testid="session-reminders-toggle"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </ProLayout>
  );
};

export default ParentDashboard;
